if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);
        this.checkboxInput.disabled = false;
        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        this.hiddenControlField.disabled = true;
        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.fromEmailInput = this.querySelector(`#Recipient-from-email-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.sendonHint = this.querySelector(`#Recipient-send-on-hint-${this.dataset.sectionId}`);
        this.sendonError = this.querySelector(`#RecipientForm-send_on-error-${this.dataset.sectionId}`);
        this.sendonErrorText = this.sendonError?.querySelector('.error-message');

        this.submitted = false;

        this.emailInput?.addEventListener('input', () => {
          if (this.submitted) this.validateOnSubmit({ focus: false });
        });

        this.sendonInput?.addEventListener('input', () => {
          if (this.submitted) this.validateOnSubmit({ focus: false });
        });

        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);
        if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;
        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
        this.sendonInput?.addEventListener('change', this.validateSendOn.bind(this));
        this.sendonInput?.addEventListener('input', this.validateSendOn.bind(this));
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId === this.dataset.sectionId) {
            this.currentProductVariantId = event.data.variant.id.toString();
          }
        });

        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }
      }

      onChange() {
        if (this.checkboxInput.checked) {
          this.enableInputFields();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
        }

        this.validateSendOn();
      }

      inputFields() {
        return [this.emailInput, this.fromEmailInput, this.messageInput, this.sendonInput];
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty];
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          this.errorMessage.innerText = this.defaultErrorHeader;
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = `${value.join(', ')}`;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');
            if (!errorTextElement) return;

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            errorTextElement.innerText = `${message}.`;
            errorMessageElement.classList.remove('hidden');

            const inputElement = this[`${key}Input`];
            if (!inputElement) return;

            inputElement.setAttribute('aria-invalid', true);
            inputElement.setAttribute('aria-describedby', errorMessageId);
          });
        }

        this.errorMessage.innerText = body;
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        this.errorMessageWrapper.hidden = true;

        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.fromEmailInput, this.sendonInput].forEach((inputElement) => {
          inputElement.setAttribute('aria-invalid', false);
          inputElement.removeAttribute('aria-describedby');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }

      getTodayISO() {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }

      isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }
      
      validateSendOn() {
        if (!this.checkboxInput.checked) {
          this.toggleSendOnHint(false);
          this.toggleSendOnError(false);
          return;
        }
      
        const value = (this.sendonInput?.value || '').trim();
      
        if (!value) {
          this.toggleSendOnError(false);
          this.toggleSendOnHint(true);
          this.sendonInput?.setAttribute('aria-invalid', false);
          this.sendonInput?.removeAttribute('aria-describedby');
          return;
        }
      
        const today = this.getTodayISO();

        if (value < today) {
          this.toggleSendOnHint(false);
          this.toggleSendOnError(true, window.recipientFormStrings?.dateErrorPast || '');
          this.sendonInput?.setAttribute('aria-invalid', true);
          this.sendonInput?.setAttribute('aria-describedby', `RecipientForm-send_on-error-${this.dataset.sectionId}`);
          return;
        }
        this.toggleSendOnHint(false);
        this.toggleSendOnError(false);
        this.sendonInput?.setAttribute('aria-invalid', false);
        this.sendonInput?.removeAttribute('aria-describedby');
      }
      
      toggleSendOnHint(show) {
        if (!this.sendonHint) return;
        this.sendonHint.classList.toggle('hidden', !show);
      }
      
      toggleSendOnError(show, message = '') {
        if (!this.sendonError) return;
        this.sendonError.classList.toggle('hidden', !show);
        if (show && this.sendonErrorText) this.sendonErrorText.textContent = message;
        if (!show && this.sendonErrorText) this.sendonErrorText.textContent = '';
      }

      validateOnSubmit({ focus = true } = {}) {
        if (!this.checkboxInput?.checked) {
          this.submitted = false;
          this.clearErrorMessage();
          return true;
        }
      
        this.submitted = true;
      
        const errors = {};
      
        const email = (this.emailInput?.value || '').trim();
        if (!email) {
          errors.email = [window.recipientFormStrings?.emailBlank || "Email can't be blank"];
          this.emailInput?.setAttribute('aria-invalid', 'true');
          this.emailInput?.setAttribute('aria-describedby', `RecipientForm-email-error-${this.dataset.sectionId}`);
        } else if (!this.isValidEmail(email)) {
          errors.email = [window.recipientFormStrings?.emailInvalid || 'Please enter a valid email address'];
          this.emailInput?.setAttribute('aria-invalid', 'true');
          this.emailInput?.setAttribute('aria-describedby', `RecipientForm-email-error-${this.dataset.sectionId}`);
        } else {
          this.emailInput?.setAttribute('aria-invalid', 'false');
          this.emailInput?.removeAttribute('aria-describedby');
        }
      
        const sendOn = (this.sendonInput?.value || '').trim();
        const today = this.getTodayISO();
        if (sendOn && sendOn < today) {
          errors.send_on = [window.recipientFormStrings?.dateErrorPast || 'Selected date has already passed.'];
          this.sendonInput?.setAttribute('aria-invalid', 'true');
          this.sendonInput?.setAttribute('aria-describedby', `RecipientForm-send_on-error-${this.dataset.sectionId}`);
        } else {
          this.sendonInput?.setAttribute('aria-invalid', 'false');
          this.sendonInput?.removeAttribute('aria-describedby');
        }
      
        if (!sendOn) {
          this.toggleSendOnError(false);
          this.toggleSendOnHint(true);
        } else if (sendOn < today) {
          this.toggleSendOnHint(false);
          this.toggleSendOnError(true, errors.send_on?.[0] || '');
        } else {
          this.toggleSendOnHint(false);
          this.toggleSendOnError(false);
        }
      
        if (Object.keys(errors).length) {
          this.displayErrorMessage(
            window.recipientFormStrings?.formErrorHeading || 'Please adjust the following:',
            errors
          );
      
          if (focus) {
            const firstKey = Object.keys(errors)[0];
            const firstEl =
              firstKey === 'email' ? this.emailInput :
              firstKey === 'send_on' ? this.sendonInput :
              null;
            firstEl?.focus();
          }
      
          return false;
        }
        this.clearErrorMessage();
        return true;
      }
    }
  );
}
